
import { useState } from "react"
import {
  Plus,
  Eye,
  Clock,
  FileText,
  Link2,
  Copy,
  Check,
  ExternalLink,
  MoreHorizontal,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { useDealRooms, useInvestors } from "@/lib/use-api"
import { createDealRoom, type ApiDealRoom } from "@/lib/api-client"
import { Skeleton } from "@/components/ui/skeleton"
import { Loader2 } from "lucide-react"

const statusStyles: Record<string, string> = {
  active: "bg-emerald-100 text-emerald-700",
  expired: "bg-red-100 text-red-600",
  draft: "bg-gray-100 text-gray-500",
}

function timeAgo(timestamp: string | null) {
  if (!timestamp) return "Never"
  const now = new Date("2026-02-06T12:00:00Z")
  const date = new Date(timestamp)
  const diffMs = now.getTime() - date.getTime()
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  if (diffHours < 1) return "Just now"
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  return `${diffDays}d ago`
}

export default function DealRoomPage() {
  const { data: apiDealRooms, isLoading, mutate } = useDealRooms()
  const { data: apiInvestors } = useInvestors()
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [isCreating, setIsCreating] = useState(false)

  // Map API deal rooms to display-friendly shape
  const dealRooms = (apiDealRooms ?? []).map((room) => {
    const inv = apiInvestors?.find((i) => i.id === room.investor_id)
    return {
      id: room.id,
      name: room.name,
      investorId: room.investor_id ?? "",
      investorName: inv?.name ?? "Unknown",
      createdAt: room.created_at,
      lastViewed: null as string | null,
      totalViews: 0,
      documentsViewed: 0,
      totalDocuments: 0,
      timeSpent: "0m",
      status: room.status as "active" | "expired" | "draft",
      accessToken: room.access_token,
    }
  })

  function handleCopyLink(id: string) {
    const room = dealRooms.find((r) => r.id === id)
    const token = room?.accessToken ?? id
    navigator.clipboard.writeText(`https://irene-investor-crm.heysalad-o.workers.dev/deal-rooms/public/${token}`)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  async function handleCreateRoom() {
    setIsCreating(true)
    try {
      await createDealRoom({ name: "New Deal Room", status: "active" })
      mutate()
    } catch {
      // silent
    } finally {
      setIsCreating(false)
    }
  }

  if (isLoading) {
    return (
      <div className="p-6 lg:p-8">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="mt-2 h-5 w-72" />
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-64 rounded-xl" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            Deal Rooms
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Shareable portals with pitch decks, metrics, and engagement tracking.
          </p>
        </div>
          <button
            type="button"
            onClick={handleCreateRoom}
            disabled={isCreating}
            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
          >
            {isCreating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            New Deal Room
          </button>
      </div>

      {/* Deal Room Cards */}
      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
        {dealRooms.map((room) => (
          <div
            key={room.id}
            className="glass-strong rounded-xl p-5 shadow-sm"
          >
            {/* Top Row */}
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-base font-semibold text-foreground">
                  {room.name}
                </h3>
                <p className="text-xs text-muted-foreground">
                  For {room.investorName}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    "rounded-full px-2.5 py-0.5 text-xs font-medium capitalize",
                    statusStyles[room.status]
                  )}
                >
                  {room.status}
                </span>
                <button
                  type="button"
                  className="rounded-lg p-1 text-muted-foreground hover:bg-accent transition-colors"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Metrics Row */}
            <div className="mt-4 grid grid-cols-4 gap-3">
              <div className="flex flex-col items-center rounded-lg bg-muted/50 p-2.5">
                <Eye className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="mt-1 text-lg font-bold text-foreground">
                  {room.totalViews}
                </span>
                <span className="text-[10px] text-muted-foreground">Views</span>
              </div>
              <div className="flex flex-col items-center rounded-lg bg-muted/50 p-2.5">
                <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="mt-1 text-lg font-bold text-foreground">
                  {room.documentsViewed}/{room.totalDocuments}
                </span>
                <span className="text-[10px] text-muted-foreground">Docs</span>
              </div>
              <div className="flex flex-col items-center rounded-lg bg-muted/50 p-2.5">
                <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="mt-1 text-lg font-bold text-foreground">
                  {room.timeSpent}
                </span>
                <span className="text-[10px] text-muted-foreground">Time</span>
              </div>
              <div className="flex flex-col items-center rounded-lg bg-muted/50 p-2.5">
                <Eye className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="mt-1 text-sm font-bold text-foreground">
                  {timeAgo(room.lastViewed)}
                </span>
                <span className="text-[10px] text-muted-foreground">Last View</span>
              </div>
            </div>

            {/* Document progress bar */}
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                <span>Document engagement</span>
                <span>
                  {room.totalDocuments > 0
                    ? Math.round((room.documentsViewed / room.totalDocuments) * 100)
                    : 0}
                  %
                </span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-muted">
                <div
                  className="h-1.5 rounded-full bg-primary transition-all"
                  style={{
                    width: `${room.totalDocuments > 0 ? (room.documentsViewed / room.totalDocuments) * 100 : 0}%`,
                  }}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="mt-4 flex items-center gap-2">
              <button
                type="button"
                onClick={() => handleCopyLink(room.id)}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-input py-2 text-xs font-medium text-foreground hover:bg-accent transition-colors"
              >
                {copiedId === room.id ? (
                  <>
                    <Check className="h-3 w-3 text-emerald-500" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-3 w-3" />
                    Copy Link
                  </>
                )}
              </button>
              <button
                type="button"
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-input py-2 text-xs font-medium text-foreground hover:bg-accent transition-colors"
              >
                <Link2 className="h-3 w-3" />
                Share
              </button>
              <button
                type="button"
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-primary py-2 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                <ExternalLink className="h-3 w-3" />
                Open
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Engagement Summary */}
      {dealRooms.length > 0 && (
        <div className="mt-6 rounded-xl border border-border bg-card p-5">
          <h3 className="text-base font-semibold text-foreground">
            Deal Room Tips
          </h3>
          <div className="mt-3 space-y-2 text-sm text-muted-foreground">
            <p>Share your deal room link with investors to track engagement in real-time.</p>
            <p>Upload your pitch deck, financials, and data room docs for a complete investor package.</p>
          </div>
        </div>
      )}
    </div>
  )
}
